import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MultiValue } from 'react-select';

import { TenDashboardService } from '../../../services/ten-dashboard/TenDashboardService';
import { TenDashboardTable } from './TenDashboardTable';
import ConfigRoutes from 'app/routing/ConfigRoutes';

const tenDashboardService = new TenDashboardService();

interface ApiOption {
  id: string | number;
  name: string;
  connectId?: number | null;
  connectName?: string | null;
  projectCode?: string | null;
  medicalAffairs?: boolean;
  type?: string | null;
}

interface SelectOption {
  value: string;
  label: string;
}

const TenDashboardMain: React.FC = () => {
  const navigate = useNavigate();
  
  const [formats, setFormats] = useState<ApiOption[]>([]);
  const [programmes, setProgrammes] = useState<ApiOption[]>([]);
  const [supporters, setSupporters] = useState<ApiOption[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [selectedFormats, setSelectedFormats] = useState<MultiValue<SelectOption>>([]);
  const [selectedProgramme, setSelectedProgramme] = useState<string>('');
  const [selectedSupporters, setSelectedSupporters] = useState<MultiValue<SelectOption>>([]);
  const [selectedComparison, setSelectedComparison] = useState<string>('');
  const [progType, setProgType] = useState<string>('both');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pData, sData] = await Promise.all([
          tenDashboardService.getProgrammes(),
          tenDashboardService.getSupporters()
        ]);

        const validProgrammes = pData as ApiOption[];

        const formatNames = Array.from(
          new Set(
            validProgrammes
              .map((programme: any) => programme.type)
              .filter(
                (type): type is string =>
                  typeof type === 'string' &&
                  type.trim() !== ''
              )
          )
        ).sort();

        const fData: ApiOption[] = formatNames.map(
          (type) => ({
            id: type,
            name: type
          })
        );

        setFormats(fData);
        setProgrammes(validProgrammes);
        setSupporters(sData as ApiOption[]);

      } catch (error) {
        console.error(
          "Error cargando datos iniciales:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      border: 0,
      borderBottom: '1px solid #dee2e6',
      borderRadius: 0,
      backgroundColor: '#f8f9fa',
      boxShadow: 'none',
      minHeight: '45px',
      '&:hover': { borderBottom: '2px solid #212529' }
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#e9ecef',
      borderRadius: '4px',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: '#495057',
      fontWeight: '500',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      '&:hover': { backgroundColor: '#dc3545', color: 'white' },
    }),
  };

  const generateReport = async () => {
    try {
      setIsGenerating(true);
      
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setMonth(fromDate.getMonth() - 6);
      
      const formatValues = selectedFormats.map(opt => opt.value);
      const supporterValues = selectedSupporters.map(opt => opt.value);
      const selectedProgrammeId = selectedProgramme ? Number(selectedProgramme) : null;
      const programmeFilter: Record<string, any> = {};

      if (formatValues.length > 0) {
        programmeFilter.type = formatValues;
      }

      if (progType === 'medAffairs') {
        programmeFilter.medicalAffairs = true;
      }

      if (progType === 'cor2ed') {
        programmeFilter.medicalAffairs = false;
      }

      const filters = {
        between_dates: {
          from: fromDate.toISOString().split('T')[0],
          to: toDate.toISOString().split('T')[0]
        },

        ...(Object.keys(programmeFilter).length > 0 && {
          programme: programmeFilter
        }),

        ...(selectedProgrammeId && {
          programmes: [selectedProgrammeId]
        }),

        ...(supporterValues.length > 0 && {
          supporters: supporterValues
        }),

        selectedBrand: progType
      };
      
      const data = await tenDashboardService.getTotalEngagement(filters);
      
      if (data) {
        navigate(ConfigRoutes.admin.tenDashboard.results.fullPath, { 
          state: { 
              reportData: data, 
              filters: { 
                selectedFormats: formatValues, 
                selectedProgramme, 
                selectedProgrammeLabel: selectedProgramme ? (programmes.find((programme) => String(programme.id) === selectedProgramme)?.name || selectedProgramme) : '',
                selectedBrand: progType,
                selectedSupporters: supporterValues,
                selectedComparison 
              } 
          } 
        });
      }
      
    } catch (error) {
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className='container-fluid mt-4'>
        <div className='row'>
          <div className='col-12'>
            <div className="text-justify-content-md-center">
              <span className='indicator-progress text-center' style={{ display: 'block' }}>
                We are loading the metrics. This may take some time...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container-fluid mt-4'>
      <div className='row'>
        <div className='col-12'>
          <TenDashboardTable
            formats={formats}
            programmes={programmes}
            supporters={supporters}
            selectedFormats={selectedFormats}
            selectedProgramme={selectedProgramme}
            selectedSupporters={selectedSupporters}
            selectedComparison={selectedComparison}
            progType={progType}
            customStyles={customSelectStyles}
            isGenerating={isGenerating}
            onFormatsChange={setSelectedFormats}
            onProgrammeChange={setSelectedProgramme}
            onSupportersChange={setSelectedSupporters}
            onComparisonChange={setSelectedComparison}
            onProgTypeChange={setProgType}
            onGenerateReport={generateReport}
          />
        </div>
      </div>
    </div>
  );
};

export { TenDashboardMain };
